export type BlobStore = {
  write(path: string, data: BlobPart): Promise<void>;
  openWritable(path: string): Promise<FileSystemWritableFileStream>;
  read(path: string): Promise<Blob>;
  readBytes(path: string): Promise<Uint8Array<ArrayBuffer>>;
  exists(path: string): Promise<boolean>;
  remove(path: string): Promise<void>;
  removeDirectory(path: string): Promise<void>;
  list(directoryPath: string): Promise<string[]>;
};

export class StorageNotFoundError extends Error {
  constructor(path: string) {
    super(`ファイルが見つかりません: ${path}`);
    this.name = "StorageNotFoundError";
  }
}

function segmentsOf(path: string): string[] {
  return path.split("/").filter((segment) => segment.length > 0);
}

async function resolveDirectory(
  root: FileSystemDirectoryHandle,
  segments: readonly string[],
  create: boolean,
): Promise<FileSystemDirectoryHandle | null> {
  let handle = root;
  for (const segment of segments) {
    try {
      handle = await handle.getDirectoryHandle(segment, { create });
    } catch {
      return null;
    }
  }
  return handle;
}

async function resolveFile(
  root: FileSystemDirectoryHandle,
  path: string,
  create: boolean,
): Promise<FileSystemFileHandle | null> {
  const segments = segmentsOf(path);
  const fileName = segments.at(-1);
  if (!fileName) return null;
  const directory = await resolveDirectory(root, segments.slice(0, -1), create);
  if (!directory) return null;
  try {
    return await directory.getFileHandle(fileName, { create });
  } catch {
    return null;
  }
}

export function createBlobStore(root: FileSystemDirectoryHandle): BlobStore {
  async function requireFile(path: string): Promise<FileSystemFileHandle> {
    const handle = await resolveFile(root, path, false);
    if (!handle) throw new StorageNotFoundError(path);
    return handle;
  }

  return {
    async write(path, data) {
      const writable = await this.openWritable(path);
      await writable.write(data);
      await writable.close();
    },

    async openWritable(path) {
      const handle = await resolveFile(root, path, true);
      if (!handle) throw new StorageNotFoundError(path);
      return handle.createWritable();
    },

    async read(path) {
      const handle = await requireFile(path);
      return handle.getFile();
    },

    async readBytes(path) {
      const file = await this.read(path);
      return new Uint8Array(await file.arrayBuffer());
    },

    async exists(path) {
      return (await resolveFile(root, path, false)) !== null;
    },

    async remove(path) {
      const segments = segmentsOf(path);
      const fileName = segments.at(-1);
      if (!fileName) return;
      const directory = await resolveDirectory(root, segments.slice(0, -1), false);
      if (!directory) return;
      await directory.removeEntry(fileName).catch(() => undefined);
    },

    async removeDirectory(path) {
      const segments = segmentsOf(path);
      const name = segments.at(-1);
      if (!name) return;
      const parent = await resolveDirectory(root, segments.slice(0, -1), false);
      if (!parent) return;
      await parent.removeEntry(name, { recursive: true }).catch(() => undefined);
    },

    async list(directoryPath) {
      const directory = await resolveDirectory(root, segmentsOf(directoryPath), false);
      if (!directory) return [];
      const names: string[] = [];
      for await (const name of directory.keys()) names.push(name);
      return names.toSorted();
    },
  };
}

export async function openOpfsStore(): Promise<BlobStore> {
  return createBlobStore(await navigator.storage.getDirectory());
}

export type StorageUsage = {
  readonly usedBytes: number;
  readonly quotaBytes: number;
};

export async function storageUsage(): Promise<StorageUsage | null> {
  const estimate = await navigator.storage?.estimate?.();
  if (!estimate) return null;
  return { usedBytes: estimate.usage ?? 0, quotaBytes: estimate.quota ?? 0 };
}

/**
 * Without this the browser may evict a whole project's media under storage pressure.
 * Chrome grants it silently for installed PWAs; elsewhere it can prompt or refuse.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  return (await navigator.storage?.persist?.()) ?? false;
}
